import { makeAutoObservable, computed, runInAction } from 'mobx'
import { Chat, ChatNotificationLevel } from '../Contracts'
import { LoadChatsTask } from '../Tasks/LoadChatsTask'
import { CreateChatTask } from '../Tasks/CreateChatTask'
import { MarkChatReadTask } from '../Tasks/MarkChatReadTask'
import { OpenEntityChatTask } from '../Tasks/OpenEntityChatTask'
import { SetChatNotificationsTask } from '../Tasks/SetChatNotificationsTask'
import {
    AddChatParticipantsTask,
    DeleteChatTask,
    LeaveChatTask,
    RemoveChatParticipantTask,
    RenameChatTask,
    UploadChatAvatarTask,
} from '../Tasks/ChatManagementTasks'
import { CHAT_TYPING_ENABLED } from '../Helpers/chatFeatures'
import { CHAT_SECTIONS } from '../Helpers/chatSections'
import type { RootStore } from './index'

/**
 * How long "typing…" stays visible without a keep-alive. The sender
 * re-signals every ~4s while typing, so a 6s TTL never flickers for an
 * active typist but dies quickly once they stop (no stop event exists).
 */
export const TYPING_TTL_MS = 6000

export class ChatStore {
    chats: Chat[] = []
    searchQuery = ''
    /** Активний розділ списку (ключ із CHAT_SECTIONS) */
    activeSection = 'all'
    isLoading = false
    /** Get-or-create чату сутності в польоті — лоадер кнопки «Чат» у заявці */
    isOpeningEntityChat = false
    /** chatId → (userId → first name) of users typing right now */
    typingByChat: Map<string, Map<string, string>> = new Map()
    error: string | null = null

    private rootStore: RootStore
    /** `${chatId}:${userId}` → TTL timer of the typing indicator */
    private typingTimeouts: Map<string, ReturnType<typeof setTimeout>> = new Map()
    /** In-flight list reload — concurrent self-healing callers await one fetch */
    private loadChatsPromise: Promise<void> | null = null
    /** chatId → trailing-таймер підтвердження читання відкритого чату */
    private readConfirmTimers: Map<string, ReturnType<typeof setTimeout>> = new Map()

    constructor(rootStore: RootStore) {
        makeAutoObservable(this, {
            sortedChats: computed,
            filteredChats: computed,
            activeChat: computed,
            totalUnreadCount: computed,
        })
        this.rootStore = rootStore
    }

    loadChats(): Promise<void> {
        // Кілька message:new невідомого чату поспіль не мають запускати
        // кілька повних перезавантажень списку — діляться одним запитом
        if (this.loadChatsPromise) return this.loadChatsPromise

        this.isLoading = true
        this.error = null
        this.loadChatsPromise = (async () => {
            try {
                const { chats, users } = await LoadChatsTask()
                runInAction(() => {
                    this.chats = chats
                    // Participants may include people outside the contacts list
                    this.rootStore.userStore.upsertUsers(users)
                })
            } catch (e) {
                runInAction(() => {
                    this.error = e instanceof Error ? e.message : 'Failed to load chats'
                })
            } finally {
                runInAction(() => {
                    this.isLoading = false
                })
                this.loadChatsPromise = null
            }
        })()

        return this.loadChatsPromise
    }

    setSearchQuery(query: string) {
        this.searchQuery = query
    }

    setActiveSection(key: string) {
        this.activeSection = key
    }

    get activeChat(): Chat | undefined {
        const { activeChatId } = this.rootStore.uiStore
        return this.chats.find((c) => c.id === activeChatId)
    }

    get sortedChats(): Chat[] {
        return [...this.chats].sort((a, b) => b.updatedAt - a.updatedAt)
    }

    get filteredChats(): Chat[] {
        // Спершу розділ (Усі/Особисті/Групи/Заявки), потім текстовий пошук
        const section = CHAT_SECTIONS.find((s) => s.key === this.activeSection)
        const sectioned = section && section.key !== 'all'
            ? this.sortedChats.filter(section.match)
            : this.sortedChats

        const q = this.searchQuery.toLowerCase().trim()
        if (!q) return sectioned

        return sectioned.filter((chat) => {
            if (chat.name && chat.name.toLowerCase().includes(q)) {
                return true
            }
            return chat.participantIds.some((pid) => {
                const name = this.rootStore.userStore.getFullName(pid)
                return name.toLowerCase().includes(q)
            })
        })
    }

    get totalUnreadCount(): number {
        // Muted chats keep their own counter but stay out of the global badge
        return this.chats.reduce(
            (sum, c) => (c.notificationLevel === 'muted' ? sum : sum + c.unreadCount),
            0
        )
    }

    getChatDisplayName(chat: Chat): string {
        if (chat.name) return chat.name
        const otherIds = chat.participantIds.filter(
            (id) => id !== this.rootStore.userStore.currentUserId
        )
        return otherIds.map((id) => this.rootStore.userStore.getFullName(id)).join(', ')
    }

    getChatAvatar(chat: Chat): string | undefined {
        if (chat.avatarUrl) return chat.avatarUrl
        if (chat.type === 'private') {
            const otherId = chat.participantIds.find(
                (id) => id !== this.rootStore.userStore.currentUserId
            )
            return otherId ? this.rootStore.userStore.getById(otherId)?.avatarUrl : undefined
        }
        return undefined
    }

    /** Add or replace a chat (POST response, chat:new realtime event) */
    upsertChat(chat: Chat) {
        const index = this.chats.findIndex((c) => c.id === chat.id)
        if (index === -1) {
            this.chats.push(chat)
        } else {
            this.chats[index] = chat
        }
    }

    /** Unread counter bump for a message arriving into an inactive chat */
    bumpUnread(chatId: string) {
        const chat = this.chats.find((c) => c.id === chatId)
        if (chat) {
            chat.unreadCount += 1
        }
    }

    /** Optimistic level change with rollback; syncs other tabs via realtime */
    async setNotificationLevel(chatId: string, level: ChatNotificationLevel) {
        const chat = this.chats.find((c) => c.id === chatId)
        if (!chat || chat.notificationLevel === level) return

        const previous = chat.notificationLevel
        chat.notificationLevel = level

        try {
            await SetChatNotificationsTask(chatId, level)
        } catch (e) {
            runInAction(() => {
                chat.notificationLevel = previous
                this.error = e instanceof Error ? e.message : 'Failed to update notifications'
            })
        }
    }

    /** Realtime: chat:notifications from another tab of the same user */
    applyNotificationLevel(chatId: string, level: ChatNotificationLevel) {
        const chat = this.chats.find((c) => c.id === chatId)
        if (chat) {
            chat.notificationLevel = level
        }
    }

    markChatAsRead(chatId: string) {
        const chat = this.chats.find((c) => c.id === chatId)
        if (chat && chat.unreadCount !== 0) {
            // Optimistic: zero locally, sync the backend in the background
            chat.unreadCount = 0
            void MarkChatReadTask(chatId).catch((error) => {
                console.warn('MarkChatReadTask error:', error)
            })
        }
    }

    /**
     * Підтвердження читання ВІДКРИТОГО чату (message:new на екрані).
     * Без guard'а по локальному бейджу — він у відкритому чаті завжди 0,
     * через що PATCH не йшов, вказівник прочитаного на беку стояв, у
     * відправника висіла вічна ✓, а бек-лічильник тихо ріс до наступного F5.
     *
     * Trailing-дебаунс: черга повідомлень дає один PATCH, і він виконується
     * ПІСЛЯ останнього з вікна — бек ставить вказівник на lastMessage чату,
     * тож жодне повідомлення не лишається непідтвердженим.
     */
    confirmChatRead(chatId: string) {
        const chat = this.chats.find((c) => c.id === chatId)
        if (!chat) return

        chat.unreadCount = 0

        if (this.readConfirmTimers.has(chatId)) return
        this.readConfirmTimers.set(
            chatId,
            setTimeout(() => {
                this.readConfirmTimers.delete(chatId)
                void MarkChatReadTask(chatId).catch((error) => {
                    console.warn('MarkChatReadTask error:', error)
                })
            }, 400)
        )
    }

    /** Local unread reset without a backend call (own read in another tab) */
    resetUnreadLocal(chatId: string) {
        const chat = this.chats.find((c) => c.id === chatId)
        if (chat) {
            chat.unreadCount = 0
        }
    }

    /** Realtime message:delivery — advance a participant's read pointer */
    applyReadState(chatId: string, userId: string, lastReadAt: number) {
        const chat = this.chats.find((c) => c.id === chatId)
        if (!chat) return

        if (!chat.readStates) chat.readStates = []
        const state = chat.readStates.find((s) => s.userId === userId)
        if (state) {
            // Тільки вперед: запізніла подія не «розчитує» назад
            state.lastReadAt = Math.max(state.lastReadAt, lastReadAt)
        } else {
            chat.readStates.push({ userId, lastReadAt })
        }
    }

    /**
     * До якого моменту чат прочитаний КИМОСЬ з інших учасників:
     * max(lastReadAt) по них — Telegram-семантика групових ✓✓
     * («прочитав хоча б один»). Учасники, які ще не відкривали чат,
     * просто ігноруються — у заявкових чатах склад насипається
     * автоматично, і мовчазний водій не має блокувати галочки всім
     * (min-варіант, WhatsApp-строгість, робив саме це).
     * У приватному чаті обидві семантики збігаються.
     */
    othersMaxReadAt(chat: Chat): number {
        const me = this.rootStore.userStore.currentUserId

        let max = 0
        for (const state of chat.readStates ?? []) {
            if (state.userId !== me && state.lastReadAt > max) {
                max = state.lastReadAt
            }
        }
        return max
    }

    /** Чи поточний користувач — власник чату (дзеркало ChatRule::isOwner) */
    isOwner(chat: Chat): boolean {
        return !!chat.ownerId && chat.ownerId === this.rootStore.userStore.currentUserId
    }

    /** Перейменування (власник) — optimistic + rollback */
    async renameChat(chatId: string, name: string) {
        const chat = this.chats.find((c) => c.id === chatId)
        const trimmed = name.trim()
        if (!chat || trimmed === '' || chat.name === trimmed) return

        const previous = chat.name
        chat.name = trimmed

        try {
            await RenameChatTask(chatId, trimmed)
        } catch (e) {
            runInAction(() => {
                if (previous !== undefined) chat.name = previous
                this.error = e instanceof Error ? e.message : 'Failed to rename chat'
            })
        }
    }

    /** Аватарка (власник): показувати нема чого до відповіді — без optimistic */
    async uploadChatAvatar(chatId: string, file: File) {
        try {
            const updated = await UploadChatAvatarTask(chatId, file)
            runInAction(() => {
                const chat = this.chats.find((c) => c.id === chatId)
                if (chat && updated.avatarUrl) {
                    chat.avatarUrl = updated.avatarUrl
                }
            })
        } catch (e) {
            runInAction(() => {
                this.error = e instanceof Error ? e.message : 'Failed to upload avatar'
            })
        }
    }

    /** Додавання учасників (власник); склад підтвердить подія chat:members */
    async addParticipants(chatId: string, userIds: string[]) {
        if (userIds.length === 0) return
        try {
            await AddChatParticipantsTask(chatId, userIds)
            runInAction(() => {
                const chat = this.chats.find((c) => c.id === chatId)
                if (chat) {
                    chat.participantIds = [...new Set([...chat.participantIds, ...userIds])]
                }
            })
        } catch (e) {
            runInAction(() => {
                this.error = e instanceof Error ? e.message : 'Failed to add participants'
            })
        }
    }

    /** Видалення учасника (власник, не себе) — optimistic + rollback */
    async removeParticipant(chatId: string, userId: string) {
        const chat = this.chats.find((c) => c.id === chatId)
        if (!chat) return

        const previous = [...chat.participantIds]
        chat.participantIds = chat.participantIds.filter((id) => id !== userId)

        try {
            await RemoveChatParticipantTask(chatId, userId)
        } catch (e) {
            runInAction(() => {
                chat.participantIds = previous
                this.error = e instanceof Error ? e.message : 'Failed to remove participant'
            })
        }
    }

    /** Вихід із чату (учасник): чат зникає локально після підтвердження */
    async leaveChat(chatId: string) {
        try {
            await LeaveChatTask(chatId)
            runInAction(() => this.removeChatLocally(chatId))
        } catch (e) {
            runInAction(() => {
                this.error = e instanceof Error ? e.message : 'Failed to leave chat'
            })
        }
    }

    /** Видалення чату (власник) */
    async deleteChat(chatId: string) {
        try {
            await DeleteChatTask(chatId)
            runInAction(() => this.removeChatLocally(chatId))
        } catch (e) {
            runInAction(() => {
                this.error = e instanceof Error ? e.message : 'Failed to delete chat'
            })
        }
    }

    /** Realtime chat:updated — назва/аватарка змінені */
    applyChatUpdated(chatId: string, name: string | null, avatarUrl: string | null) {
        const chat = this.chats.find((c) => c.id === chatId)
        if (!chat) return

        if (name != null) chat.name = name
        if (avatarUrl != null) {
            chat.avatarUrl = avatarUrl
        } else {
            delete chat.avatarUrl
        }
    }

    /**
     * Realtime chat:members. Якщо серед видалених — я, чат зникає цілком
     * (доступ уже втрачено на беку); інакше просто оновлюється склад.
     */
    applyChatMembers(chatId: string, participantUuids: string[], removedUserUuids: string[]) {
        const me = this.rootStore.userStore.currentUserId
        if (removedUserUuids.includes(me)) {
            this.removeChatLocally(chatId)
            return
        }

        const chat = this.chats.find((c) => c.id === chatId)
        if (chat && participantUuids.length > 0) {
            chat.participantIds = participantUuids
        }
    }

    /** Realtime chat:deleted */
    applyChatDeleted(chatId: string) {
        this.removeChatLocally(chatId)
    }

    /** Прибирає чат зі сторів (список, повідомлення, активний екран) */
    private removeChatLocally(chatId: string) {
        this.chats = this.chats.filter((c) => c.id !== chatId)
        this.rootStore.messageStore.removeChatMessages(chatId)

        if (this.rootStore.uiStore.activeChatId === chatId) {
            this.rootStore.uiStore.setActiveChatId(null)
        }
    }

    /** Realtime typing:start — show the indicator until the TTL or a message */
    setTyping(chatId: string, userId: string, name: string) {
        if (userId === this.rootStore.userStore.currentUserId) return

        let users = this.typingByChat.get(chatId)
        if (!users) {
            users = new Map()
            this.typingByChat.set(chatId, users)
        }
        users.set(userId, name)

        // Keep-alive переставляє TTL-таймер конкретного користувача
        const key = `${chatId}:${userId}`
        const existing = this.typingTimeouts.get(key)
        if (existing) clearTimeout(existing)
        this.typingTimeouts.set(
            key,
            setTimeout(() => this.clearTyping(chatId, userId), TYPING_TTL_MS)
        )
    }

    /** Hide the indicator (TTL expired or the author's message arrived) */
    clearTyping(chatId: string, userId: string) {
        const key = `${chatId}:${userId}`
        const timeout = this.typingTimeouts.get(key)
        if (timeout) {
            clearTimeout(timeout)
            this.typingTimeouts.delete(key)
        }
        this.typingByChat.get(chatId)?.delete(userId)
    }

    /** First names of users typing in the chat right now */
    typingNames(chatId: string): string[] {
        const users = this.typingByChat.get(chatId)
        return users ? Array.from(users.values()) : []
    }

    /**
     * Єдине місце формулювання «друкує…» (шапка і список чатів мають
     * показувати одне й те саме). У приваті ім'я зайве — воно в заголовку;
     * verbose (шапка) перелічує всіх, компакт (список) — лише першого.
     */
    typingLabel(chat: Chat, verbose = false): string | null {
        if (!CHAT_TYPING_ENABLED) return null

        const names = this.typingNames(chat.id)
        if (names.length === 0) return null
        if (chat.type !== 'group') return 'друкує…'
        if (!verbose) return `${names[0] ?? ''} друкує…`

        return `${names.join(', ')} ${names.length > 1 ? 'друкують' : 'друкує'}…`
    }

    updateLastMessage(chatId: string, text: string, senderId: string, timestamp = Date.now()) {
        const chat = this.chats.find((c) => c.id === chatId)
        if (chat) {
            chat.lastMessage = { text, senderId, timestamp }
            chat.updatedAt = timestamp
        }
    }

    /** Find existing private chat with a user, or return undefined */
    findPrivateChatWith(userId: string): Chat | undefined {
        const currentId = this.rootStore.userStore.currentUserId
        return this.chats.find(
            (c) =>
                c.type === 'private' &&
                c.participantIds.includes(currentId) &&
                c.participantIds.includes(userId)
        )
    }

    /**
     * Create (or open existing) private chat with a user. The local lookup is
     * a shortcut only — the backend deduplicates the pair anyway and returns
     * the existing chat, so a stale local list can not create a duplicate.
     */
    async createPrivateChat(userId: string) {
        const existing = this.findPrivateChatWith(userId)
        if (existing) {
            this.rootStore.uiStore.setActiveChatId(existing.id)
            return
        }

        try {
            const chat = await CreateChatTask({ type: 'private', participantId: userId })
            runInAction(() => {
                this.upsertChat(chat)
                this.rootStore.uiStore.setActiveChatId(chat.id)
            })
        } catch (e) {
            runInAction(() => {
                this.error = e instanceof Error ? e.message : 'Failed to create chat'
            })
        }
    }

    /**
     * Кнопка «Чат» у картці сутності (заявки): get-or-create на бекенді,
     * потім чат стає активним і дровер відкривається. Ідемпотентно —
     * повторні кліки повертають той самий чат.
     */
    async openEntityChat(entityType: string, entityUuid: string) {
        // Дабл-клік по кнопці, поки перший запит у польоті — ігнорується
        if (this.isOpeningEntityChat) return
        this.isOpeningEntityChat = true

        // Дровер відкривається ОДРАЗУ, get-or-create летить під його
        // оверлей-спінером (див. ChatDrawer): миттєвий фідбек замість
        // мертвої кнопки на час запиту
        this.rootStore.uiStore.openDrawer()

        try {
            const { chat, users } = await OpenEntityChatTask(entityType, entityUuid)
            runInAction(() => {
                // Учасники з заявки можуть бути поза списком контактів
                this.rootStore.userStore.upsertUsers(users)
                this.upsertChat(chat)
                this.rootStore.uiStore.setActiveChatId(chat.id)
            })
        } catch (e) {
            runInAction(() => {
                this.error = e instanceof Error ? e.message : 'Failed to open entity chat'
            })
        } finally {
            runInAction(() => {
                this.isOpeningEntityChat = false
            })
        }
    }

    /** Create a group chat with multiple users */
    async createGroupChat(userIds: string[], name: string) {
        try {
            const chat = await CreateChatTask({ type: 'group', participantIds: userIds, name })
            runInAction(() => {
                this.upsertChat(chat)
                this.rootStore.uiStore.setActiveChatId(chat.id)
            })
        } catch (e) {
            runInAction(() => {
                this.error = e instanceof Error ? e.message : 'Failed to create chat'
            })
        }
    }
}
